import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';

import { t } from '../../i18n-t';
import { issueTypeTaxonomy } from '../issues/variants';
import type { DefaultFieldCondition, FormConfig } from '../types';

const showWhenYes = (ifField: string): DefaultFieldCondition => ({
  readOnly: true,
  rules: [{ ifField, values: ['true'] }],
});

export const getIssueAssessmentFormConfig = (
  parentIssueType: ParentIssueType,
  formSectionsEnabled: boolean
) => {
  // Surfacing these regulation/policy targets as condition sources + valid
  // targets unblocks the read-only seeded rule in the configure-form editor,
  // but that affordance only makes sense when sectioned rendering is on.
  // Flag-off prod behaviour stays exactly as before.
  const conditionableWhenSectioned = formSectionsEnabled
    ? ({ allowAsConditionSource: true, allowTargetConditions: true } as const)
    : ({} as const);
  const taxonomyLookup = issueTypeTaxonomy[parentIssueType];
  const assessmentTaxonomy = taxonomyLookup.assessmentTaxonomy;
  const taxonomy = taxonomyLookup.taxonomy;

  return {
    IssueType: {
      fieldId: 'IssueType',
      formLabel: t(`${assessmentTaxonomy}.fields.IssueType`),
      columnHeader: t(`${taxonomy}.columns.type`),
      displayType: {
        displayType: 'commonLookup',
        i18nKey: 'issueTypes',
      },
      allowAsConditionSource: true,
      allowTargetConditions: true,
    },
    Status: {
      fieldId: 'Status',
      formLabel: t(`fields.Status`), // From shared i18n
      columnHeader: t(`${taxonomy}.columns.status`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      displayType: {
        displayType: 'rating',
        ratingKey: 'issue_assessment_status',
      },
    },
    Severity: {
      fieldId: 'Severity',
      formLabel: t(`${assessmentTaxonomy}.fields.Severity`),
      columnHeader: t(`${taxonomy}.columns.severity`),
      displayType: { displayType: 'rating', ratingKey: 'severity' },
      allowAsConditionSource: true,
      allowTargetConditions: true,
    },
    TargetCloseDate: {
      fieldId: 'TargetCloseDate',
      formLabel: t(`${assessmentTaxonomy}.fields.TargetCloseDate`),
      columnHeader: t(`${taxonomy}.columns.target_close_date`),
      displayType: { displayType: 'date' },
      allowAsConditionSource: true,
      allowTargetConditions: true,
    },
    ActualCloseDate: {
      fieldId: 'ActualCloseDate',
      formLabel: t(`${assessmentTaxonomy}.fields.ActualCloseDate`),
      columnHeader: t(`${taxonomy}.columns.actual_close_date`),
      displayType: { displayType: 'date' },
      allowAsConditionSource: true,
      allowTargetConditions: true,
      // Read-only: an actual close date only exists once the assessment is closed.
      defaultConditions: {
        readOnly: true,
        rules: [{ ifField: 'Status', values: ['closed'] }],
      },
    },
    AssociatedControlIds: {
      fieldId: 'AssociatedControlIds',
      formLabel: t(`${assessmentTaxonomy}.fields.AssociatedControls`),
    },
    CertifiedIndividual: {
      fieldId: 'CertifiedIndividual',
      formLabel: t(`${assessmentTaxonomy}.fields.CertifiedIndividual`),
      columnHeader: t(`${taxonomy}.columns.certified_individual`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      displayType: {
        displayType: 'users',
        multiple: true,
      },
    },
    RegulatoryBreach: {
      fieldId: 'RegulatoryBreach',
      formLabel: t(`${assessmentTaxonomy}.fields.RegulatoryBreach`),
      columnHeader: t(`${taxonomy}.columns.regulatory_breach`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      displayType: { displayType: 'commonLookup', i18nKey: 'yesOrNo' },
    },
    RegulationsBreached: {
      fieldId: 'RegulationsBreached',
      formLabel: t(`${assessmentTaxonomy}.fields.RegulationsBreached`),
      columnHeader: t(`${taxonomy}.columns.regulations_breached`),
      ...conditionableWhenSectioned,
      visibilityControlledByFieldId: 'RegulatoryBreach',
      defaultConditions: showWhenYes('RegulatoryBreach'),
    },
    RegulationsBreachedIds: {
      fieldId: 'RegulationsBreachedIds',
      formLabel: t(`${assessmentTaxonomy}.fields.RegulationsBreached`),
      columnHeader: t(`${taxonomy}.columns.regulations_breached`),
      ...conditionableWhenSectioned,
      visibilityControlledByFieldId: 'RegulatoryBreach',
      defaultConditions: showWhenYes('RegulatoryBreach'),
    },
    departments: {
      fieldId: 'departments',
      formLabel: t(`fields.Departments`), // From shared i18n
      columnHeader: t(`${taxonomy}.columns.assessment_departments`),
      displayType: {
        displayType: 'departments',
      },
      allowAsConditionSource: true,
      allowTargetConditions: true,
    },
    PolicyBreach: {
      fieldId: 'PolicyBreach',
      formLabel: t(`${assessmentTaxonomy}.fields.PolicyBreach`),
      columnHeader: t(`${taxonomy}.columns.policy_breach`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      displayType: { displayType: 'commonLookup', i18nKey: 'yesOrNo' },
    },
    PoliciesBreached: {
      fieldId: 'PoliciesBreached',
      formLabel: t(`${assessmentTaxonomy}.fields.PoliciesBreached`),
      columnHeader: t(`${taxonomy}.columns.policies_breached`),
      ...conditionableWhenSectioned,
      visibilityControlledByFieldId: 'PolicyBreach',
      defaultConditions: showWhenYes('PolicyBreach'),
    },
    PoliciesBreachedIds: {
      fieldId: 'PoliciesBreachedIds',
      formLabel: t(`${assessmentTaxonomy}.fields.PoliciesBreached`),
      columnHeader: t(`${taxonomy}.columns.policies_breached`),
      ...conditionableWhenSectioned,
      visibilityControlledByFieldId: 'PolicyBreach',
      defaultConditions: showWhenYes('PolicyBreach'),
    },
    PolicyOwner: {
      fieldId: 'PolicyOwner',
      formLabel: t(`${assessmentTaxonomy}.fields.PolicyOwner`),
      columnHeader: t(`${taxonomy}.columns.policy_owner`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      displayType: {
        displayType: 'users',
        multiple: true,
      },
      visibilityControlledByFieldId: 'PolicyBreach',
      defaultConditions: showWhenYes('PolicyBreach'),
    },
    PolicyOwnerCommentary: {
      fieldId: 'PolicyOwnerCommentary',
      formLabel: t(`${assessmentTaxonomy}.fields.PolicyOwnerCommentary`),
      columnHeader: t(`${taxonomy}.columns.policy_owner_commentary`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      visibilityControlledByFieldId: 'PolicyBreach',
      defaultConditions: showWhenYes('PolicyBreach'),
      displayType: { displayType: 'textarea' },
    },
    tags: {
      fieldId: 'tags',
      formLabel: t(`fields.Tags`), // From shared i18n
      displayType: {
        displayType: 'tags',
      },
      allowAsConditionSource: true,
      allowTargetConditions: true,
    },
    IssueCausedBySystemIssue: {
      fieldId: 'IssueCausedBySystemIssue',
      formLabel: t(`${assessmentTaxonomy}.fields.IssueCausedBySystemIssue`),
      columnHeader: t(`${taxonomy}.columns.caused_by_system_issue`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      displayType: { displayType: 'commonLookup', i18nKey: 'yesOrNo' },
    },
    SystemResponsible: {
      fieldId: 'SystemResponsible',
      formLabel: t(`${assessmentTaxonomy}.fields.SystemResponsible`),
      columnHeader: t(`${taxonomy}.columns.system_responsible`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      visibilityControlledByFieldId: 'IssueCausedBySystemIssue',
      defaultConditions: showWhenYes('IssueCausedBySystemIssue'),
    },
    Reportable: {
      fieldId: 'Reportable',
      formLabel: t(`${assessmentTaxonomy}.fields.Reportable`),
      columnHeader: t(`${taxonomy}.columns.reportable`),
      displayType: { displayType: 'commonLookup', i18nKey: 'yesOrNo' },
      allowAsConditionSource: true,
      allowTargetConditions: true,
      visibilityControlledByFieldId: 'RegulatoryBreach',
      defaultConditions: showWhenYes('RegulatoryBreach'),
    },
    Rationale: {
      fieldId: 'Rationale',
      formLabel: t(`${assessmentTaxonomy}.fields.Rationale`),
      columnHeader: t(`${taxonomy}.columns.rationale`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      visibilityControlledByFieldId: 'RegulatoryBreach',
      defaultConditions: showWhenYes('RegulatoryBreach'),
      displayType: { displayType: 'textarea' },
    },
    IssueCausedByThirdParty: {
      fieldId: 'IssueCausedByThirdParty',
      formLabel: t(`${assessmentTaxonomy}.fields.IssueCausedByThirdParty`),
      columnHeader: t(`${taxonomy}.columns.issue_caused_by_third_party`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      displayType: { displayType: 'commonLookup', i18nKey: 'yesOrNo' },
    },
    ThirdPartyResponsible: {
      fieldId: 'ThirdPartyResponsible',
      formLabel: t(`${assessmentTaxonomy}.fields.ThirdPartyResponsible`),
      columnHeader: t(`${taxonomy}.columns.third_party_responsible`),
      allowAsConditionSource: true,
      allowTargetConditions: true,
      visibilityControlledByFieldId: 'IssueCausedByThirdParty',
      defaultConditions: showWhenYes('IssueCausedByThirdParty'),
    },
  } as const satisfies FormConfig;
};
